/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { DC3NotificationSettings, DC3Record, UserProfile, CostCenter } from '../types.ts';
import { NotificationService } from '../lib/notificationService.ts';
import { fcmService } from '../lib/fcmService.ts';
import { dataService } from '../lib/dataService.ts';

export const DEFAULT_DC3_NOTIFICATION_SETTINGS: DC3NotificationSettings = {
  enabled: true,
  channels: {
    browserPush: true,
    fcmPush: true,
    audioChime: true,
    emailDigest: true,
    inAppBanner: true,
    whatsappAlerts: false
  },
  frequency: 'daily',
  dailyTime: '08:00',
  weeklyDay: 1, // Lunes
  leadDays: {
    days60: true,  // 60 días antes (Preventivo)
    days30: true,  // 30 días antes (1 mes - Estándar STPS)
    days15: true,  // 15 días antes (Crítico)
    days7: true,   // 7 días antes (Urgente)
    days0: true,   // Día de vencimiento
    expired: true  // Vencidas
  },
  targetCostCenterMode: 'assigned',
  filterCriticalNormsOnly: false,
  minComplianceThresholdPercent: 90,
  emailRecipients: '',
  phoneRecipient: ''
};

export function getDefaultDC3NotificationSettings(): DC3NotificationSettings {
  return JSON.parse(JSON.stringify(DEFAULT_DC3_NOTIFICATION_SETTINGS));
}

// Aggregates DC3 records across all cost center caches and global storage
export function loadAllDC3RecordsFromStorage(costCenters: CostCenter[] = []): DC3Record[] {
  if (typeof window === 'undefined') return [];
  const map = new Map<string, DC3Record>();

  // 1. Check global snfsic_dc3Records
  try {
    const globalRaw = localStorage.getItem('snfsic_dc3Records') || localStorage.getItem('snfsic_dc3');
    if (globalRaw) {
      const parsed: DC3Record[] = JSON.parse(globalRaw);
      parsed.forEach(r => {
        if (r && r.id) map.set(r.id, r);
      });
    }
  } catch (e) {
    console.warn('Failed reading global dc3 records', e);
  }

  // 2. Scan cost center specific storage keys (snf_sec_v3_<ccId>_dc3)
  const ccIds = new Set<string>(['default', 'CC-MAZDA-BAJIO', 'CC-RONAL-QRO', 'CC-BMW-SLP', 'CC-BMW-PUE', 'CC-PROMAQ-SLT', 'CC-PROMAQ-MTY']);
  costCenters.forEach(cc => {
    if (cc.id) ccIds.add(cc.id);
  });

  ccIds.forEach(id => {
    try {
      const key = `snf_sec_v3_${id}_dc3`;
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed: DC3Record[] = JSON.parse(raw);
        parsed.forEach(r => {
          if (r && r.id) {
            if (!r.costCenterId && id !== 'default') {
              r.costCenterId = id;
            }
            map.set(r.id, r);
          }
        });
      }
    } catch (e) {
      // Ignore
    }
  });

  return Array.from(map.values());
}

// Critical STPS safety standards
export const CRITICAL_STPS_NORMS = [
  'NOM-009', // Trabajos en altura
  'NOM-017', // EPP
  'NOM-018', // Químicos
  'NOM-027', // Soldadura y corte
  'NOM-029', // Mantenimiento eléctrico
  'NOM-033', // Espacios confinados
  'NOM-004', // Maquinaria y equipo
  'NOM-002', // Prevención contra incendios
  'NOM-006'  // Manejo de materiales
];

export interface DC3AlertItem {
  record: DC3Record;
  employeeName: string;
  courseName: string;
  daysRemaining: number;
  expiryDate: string;
  triggerType: '60d' | '30d' | '15d' | '7d' | '0d' | 'expired';
  severity: 'info' | 'warning' | 'critical';
  badgeLabel: string;
}

export interface DC3EvaluationResult {
  totalAnalyzed: number;
  alertItems: DC3AlertItem[];
  expiringIn30DaysCount: number;
  expiredCount: number;
  compliancePercent: number;
  shouldNotify: boolean;
  reason: string;
}

// Evaluates a single record against the settings
export function evaluateDC3Records(
  records: DC3Record[],
  settings: DC3NotificationSettings,
  userCostCenters?: CostCenter[]
): DC3EvaluationResult {
  if (!records || records.length === 0) {
    return {
      totalAnalyzed: 0,
      alertItems: [],
      expiringIn30DaysCount: 0,
      expiredCount: 0,
      compliancePercent: 100,
      shouldNotify: false,
      reason: 'No hay registros DC-3 disponibles para evaluar.'
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // 1. Filter by Cost Center Mode
  let filtered = [...records];
  if (settings.targetCostCenterMode === 'assigned' && userCostCenters && userCostCenters.length > 0) {
    const allowedIds = new Set(userCostCenters.map(cc => cc.id.toLowerCase()));
    filtered = filtered.filter(r => !r.costCenterId || allowedIds.has(r.costCenterId.toLowerCase()));
  } else if (settings.targetCostCenterMode === 'custom' && settings.customCostCenterIds && settings.customCostCenterIds.length > 0) {
    const customIds = new Set(settings.customCostCenterIds.map(id => id.toLowerCase()));
    filtered = filtered.filter(r => r.costCenterId && customIds.has(r.costCenterId.toLowerCase()));
  }

  // 2. Filter by Critical Norms if enabled
  if (settings.filterCriticalNormsOnly) {
    filtered = filtered.filter(r => {
      const course = (r.courseName || '').toUpperCase();
      return CRITICAL_STPS_NORMS.some(norm => course.includes(norm));
    });
  }

  const alertItems: DC3AlertItem[] = [];
  let expiredCount = 0;
  let expiringIn30DaysCount = 0;

  filtered.forEach(rec => {
    // Calculate validity
    let expiryStr = rec.expiryDate;
    if (!expiryStr && rec.issueDate) {
      const issue = new Date(rec.issueDate);
      if (!isNaN(issue.getTime())) {
        const nextYear = new Date(issue);
        nextYear.setFullYear(nextYear.getFullYear() + 1);
        expiryStr = nextYear.toISOString().split('T')[0];
      }
    }

    if (!expiryStr) return;

    const expiryDate = new Date(expiryStr);
    expiryDate.setHours(0, 0, 0, 0);
    const diffTime = expiryDate.getTime() - today.getTime();
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (daysRemaining < 0) {
      expiredCount++;
      if (settings.leadDays.expired) {
        alertItems.push({
          record: rec,
          employeeName: rec.employeeName || 'Colaborador',
          courseName: rec.courseName || 'Constancia STPS',
          daysRemaining,
          expiryDate: expiryStr,
          triggerType: 'expired',
          severity: 'critical',
          badgeLabel: `Vencida (${Math.abs(daysRemaining)} días atrás)`
        });
      }
    } else if (daysRemaining === 0) {
      expiringIn30DaysCount++;
      if (settings.leadDays.days0) {
        alertItems.push({
          record: rec,
          employeeName: rec.employeeName || 'Colaborador',
          courseName: rec.courseName || 'Constancia STPS',
          daysRemaining: 0,
          expiryDate: expiryStr,
          triggerType: '0d',
          severity: 'critical',
          badgeLabel: 'Vence HOY'
        });
      }
    } else if (daysRemaining <= 7) {
      expiringIn30DaysCount++;
      if (settings.leadDays.days7) {
        alertItems.push({
          record: rec,
          employeeName: rec.employeeName || 'Colaborador',
          courseName: rec.courseName || 'Constancia STPS',
          daysRemaining,
          expiryDate: expiryStr,
          triggerType: '7d',
          severity: 'critical',
          badgeLabel: `Vence en ${daysRemaining} días`
        });
      }
    } else if (daysRemaining <= 15) {
      expiringIn30DaysCount++;
      if (settings.leadDays.days15) {
        alertItems.push({
          record: rec,
          employeeName: rec.employeeName || 'Colaborador',
          courseName: rec.courseName || 'Constancia STPS',
          daysRemaining,
          expiryDate: expiryStr,
          triggerType: '15d',
          severity: 'warning',
          badgeLabel: `Vence en ${daysRemaining} días (Crítico)`
        });
      }
    } else if (daysRemaining <= 30) {
      expiringIn30DaysCount++;
      if (settings.leadDays.days30) {
        alertItems.push({
          record: rec,
          employeeName: rec.employeeName || 'Colaborador',
          courseName: rec.courseName || 'Constancia STPS',
          daysRemaining,
          expiryDate: expiryStr,
          triggerType: '30d',
          severity: 'warning',
          badgeLabel: `Vence en ${daysRemaining} días (1 mes)`
        });
      }
    } else if (daysRemaining <= 60) {
      if (settings.leadDays.days60) {
        alertItems.push({
          record: rec,
          employeeName: rec.employeeName || 'Colaborador',
          courseName: rec.courseName || 'Constancia STPS',
          daysRemaining,
          expiryDate: expiryStr,
          triggerType: '60d',
          severity: 'info',
          badgeLabel: `Vence en ${daysRemaining} días (Preventivo)`
        });
      }
    }
  });

  const totalValid = filtered.length - expiredCount;
  const compliancePercent = filtered.length > 0 ? Math.round((totalValid / filtered.length) * 100) : 100;

  const belowThreshold = settings.minComplianceThresholdPercent
    ? compliancePercent < settings.minComplianceThresholdPercent
    : false;

  const shouldNotify = settings.enabled && (alertItems.length > 0 || belowThreshold);
  let reason = '';
  if (!settings.enabled) {
    reason = 'Notificaciones DC-3 deshabilitadas por el usuario.';
  } else if (alertItems.length > 0) {
    reason = `Se detectaron ${alertItems.length} constancias en ventana de atención.`;
  } else if (belowThreshold) {
    reason = `Cumplimiento normativo (${compliancePercent}%) inferior al umbral configurado (${settings.minComplianceThresholdPercent}%).`;
  } else {
    reason = 'Todas las constancias evaluadas se encuentran al corriente.';
  }

  return {
    totalAnalyzed: filtered.length,
    alertItems,
    expiringIn30DaysCount,
    expiredCount,
    compliancePercent,
    shouldNotify,
    reason
  };
}

// Dispatches real-time alerts across enabled push/audio channels
export async function dispatchDC3NotificationAlerts(
  evaluation: DC3EvaluationResult,
  settings: DC3NotificationSettings,
  userProfile?: UserProfile | null
): Promise<{ success: boolean; channelsFired: string[] }> {
  if (!settings.enabled || !evaluation.shouldNotify || evaluation.alertItems.length === 0) {
    return { success: false, channelsFired: [] };
  }

  const channelsFired: string[] = [];
  const topAlert = evaluation.alertItems[0];
  const totalAlerts = evaluation.alertItems.length;

  const title = totalAlerts === 1
    ? `⚠️ Formato DC-3 por Vencer: ${topAlert.employeeName}`
    : `🚨 Alerta STPS: ${totalAlerts} Constancias DC-3 por Vencer`;

  const body = totalAlerts === 1
    ? `${topAlert.courseName} - ${topAlert.badgeLabel}. Planifica la re-capacitación STPS.`
    : `Se detectaron ${evaluation.expiringIn30DaysCount} por vencer y ${evaluation.expiredCount} vencidas. Índice de cumplimiento: ${evaluation.compliancePercent}%.`;

  // 1. Audio Synthesizer Chime
  if (settings.channels.audioChime) {
    try {
      const chimeType = evaluation.expiredCount > 0 ? 'critical' : 'warning';
      NotificationService.playChime(chimeType);
      channelsFired.push('audioChime');
    } catch (e) {
      console.warn('[DC3 Notification] Audio chime failed:', e);
    }
  }

  // 2. Web Browser Native Push
  if (settings.channels.browserPush) {
    try {
      const fired = NotificationService.sendNotification(
        title,
        {
          body,
          tag: `dc3-alert-${Date.now()}`,
          requireInteraction: evaluation.expiredCount > 0
        },
        evaluation.expiredCount > 0 ? 'critical' : 'warning'
      );
      if (fired) channelsFired.push('browserPush');
    } catch (e) {
      console.warn('[DC3 Notification] Native push failed:', e);
    }
  }

  // 3. Firebase Cloud Messaging (FCM)
  if (settings.channels.fcmPush) {
    try {
      await fcmService.sendCriticalStatusNotification({
        title,
        body,
        category: 'URGENTE',
        url: '/#interno?sec=formatos_dc3'
      });
      channelsFired.push('fcmPush');
    } catch (e) {
      console.warn('[DC3 Notification] FCM dispatch failed:', e);
    }
  }

  // 4. In-App Banner Event
  if (settings.channels.inAppBanner && typeof window !== 'undefined') {
    const customEvent = new CustomEvent('dc3_expiration_alert', {
      detail: {
        title,
        body,
        evaluation
      }
    });
    window.dispatchEvent(customEvent);
    channelsFired.push('inAppBanner');
  }

  // 5. Update lastNotifiedAt timestamp
  try {
    const updatedSettings = {
      ...settings,
      lastNotifiedAt: new Date().toISOString()
    };
    if (userProfile && userProfile.uid) {
      await dataService.saveUser({
        ...userProfile,
        dc3NotificationSettings: updatedSettings
      });
    }
    localStorage.setItem('snfsic_dc3_notif_config', JSON.stringify(updatedSettings));
  } catch (e) {
    console.warn('[DC3 Notification] Failed updating lastNotifiedAt:', e);
  }

  return { success: true, channelsFired };
}

// Generate preformatted WhatsApp message
export function buildDC3WhatsAppAlertUrl(evaluation: DC3EvaluationResult, phone?: string): string {
  const targetPhone = (phone || '').replace(/[^\d+]/g, '');
  const lines = [
    `*🚨 ALERTA DE CUMPLIMIENTO STPS - FORMATOS DC-3*`,
    `_Sistema SNF - SIC | Seguridad Industrial_`,
    ``,
    `📊 *Resumen de Cumplimiento:*`,
    `• Total constancias evaluadas: ${evaluation.totalAnalyzed}`,
    `• Próximas a vencer (<30 días): ${evaluation.expiringIn30DaysCount}`,
    `• Vencidas: ${evaluation.expiredCount}`,
    `• Cumplimiento global: ${evaluation.compliancePercent}%`,
    ``,
    `📋 *Colaboradores con atención prioritaria:*`
  ];

  evaluation.alertItems.slice(0, 6).forEach((item, idx) => {
    lines.push(`${idx + 1}. *${item.employeeName}* - ${item.courseName}`);
    lines.push(`   └ ${item.badgeLabel} (Vence: ${item.expiryDate})`);
  });

  if (evaluation.alertItems.length > 6) {
    lines.push(`... y ${evaluation.alertItems.length - 6} constancias adicionales.`);
  }

  lines.push(``);
  lines.push(`Favor de coordinar la re-capacitación y emisión de constancias DC-3 con el Agente Capacitador Externo/Interno.`);

  const textEncoded = encodeURIComponent(lines.join('\n'));
  return targetPhone ? `https://wa.me/${targetPhone}?text=${textEncoded}` : `https://wa.me/?text=${textEncoded}`;
}
